import { EarningsInsight } from '@api';
import { Icon, IconProps } from '@design/components/icon/Icon';
import { formatPercentage, formatPriceWithCurrency } from '@helpers';
import { useGetEarningById } from '@terminal/react-query-hooks/earnings/useGetEarningById';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';

import styles from './EarningsInsightTooltip.module.scss';

interface EarningsInsightTooltipProps {
  earningsInsight: EarningsInsight;
  isPreview: boolean;
  iconProps: IconProps;
}

export const EarningsInsightTooltip = ({
  isPreview,
  earningsInsight,
  iconProps,
}: EarningsInsightTooltipProps) => {
  const { t } = useTranslation(['earnings']);
  const { data: earningsData } = useGetEarningById(earningsInsight.earnings_id);

  const title = isPreview
    ? t('earnings:earningsInsight.drawer.earningsPreview.title')
    : t('earnings:earningsInsight.drawer.earningsRecap.title');
  const epsLabel = isPreview
    ? t('earnings:earningsInsight.drawer.epsExpectations')
    : t('earnings:earningsInsight.drawer.epsActual');
  const epsValue = isPreview
    ? earningsData?.eps?.estimate
    : earningsData?.eps?.value;
  const deltaValue = earningsData?.eps?.surprise_percentage;

  const isNegativeDelta = !!deltaValue && deltaValue < 0;

  return (
    <div className={styles.tooltipWrapper}>
      <Icon
        iconName={iconProps.iconName}
        viewBox={iconProps.viewBox}
        size={32}
      />
      <div className={styles.rightWrapper}>
        <div className={styles.titleLabel}>{title}</div>
        <div className={styles.bottomWrapper}>
          {epsValue !== undefined && (
            <>
              <div className={styles.descriptionLabel}>{epsLabel}</div>
              <div className={styles.valueWrapper}>
                {formatPriceWithCurrency(epsValue, earningsData?.currency)}
              </div>
            </>
          )}
          {!isPreview && !!deltaValue && (
            <div className={styles.recapWrapper}>
              <span
                className={clsx(styles.recapValue, {
                  [styles.recapValueBearish]: isNegativeDelta,
                  [styles.recapValueBullish]: !isNegativeDelta,
                })}
              >
                {formatPercentage(deltaValue, {
                  suffix: '%',
                  addPlus: !isNegativeDelta,
                  useOriginalValue: true,
                })}
              </span>
              <span className={styles.recapLabel}>
                {t('earnings:earningsInsight.drawer.vsConsExp')}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
